import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import {
  ConnectComponentsProvider,
  ConnectBalances,
  ConnectAccountManagement,
} from '@stripe/react-connect-js';
import { supabase } from '../supabase';
import theme from '../theme';
import BottomNav from '../BottomNav';

export default function StripeAccountManager({ session }) {
  const navigate = useNavigate();
  const [stripeConnectInstance, setStripeConnectInstance] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [payingOut, setPayingOut] = useState(false);
  const [payoutMessage, setPayoutMessage] = useState(null);

  const fetchClientSecret = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke('create-account-session');
    if (error || !data?.client_secret) {
      throw new Error(data?.error || 'Errore nel recupero della sessione Stripe');
    }
    return data.client_secret;
  }, []);

  const fetchBalance = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke('get-stripe-balance');
    if (!error && data && !data.error) {
      setBalance(data);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const instance = loadConnectAndInitialize({
          publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
          fetchClientSecret,
          appearance: {
            variables: {
              colorPrimary: theme.colors.primary,
            },
          },
        });
        if (mounted) {
          setStripeConnectInstance(instance);
          setLoading(false);
        }
      } catch (err) {
        console.error('Errore inizializzazione Stripe Connect:', err);
        if (mounted) {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    init();
    fetchBalance();
    return () => { mounted = false; };
  }, [fetchClientSecret, fetchBalance]);

  const handlePayout = async () => {
    setPayingOut(true);
    setPayoutMessage(null);
    const { data, error } = await supabase.functions.invoke('trigger-payout');
    setPayingOut(false);

    if (error || data?.error) {
      setPayoutMessage({ type: 'error', text: data?.error || 'Errore durante il prelievo. Riprova.' });
      return;
    }

    setPayoutMessage({ type: 'success', text: `Prelievo di €${(data.amount / 100).toFixed(2)} avviato. Arriverà sul tuo IBAN nei prossimi giorni lavorativi.` });
    fetchBalance();
  };

  const availableEuro = balance ? (balance.available / 100).toFixed(2) : null;
  const canPayout = balance && balance.available > 0;

  return (
    <div style={{ maxWidth: theme.layout.maxWidth, margin: '0 auto', fontFamily: theme.font.family, minHeight: '100vh', background: theme.colors.background || '#fff' }}>
      <div style={{ background: theme.colors.primary, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
        <button onClick={() => navigate('/pagamenti')} style={{ background: 'none', border: 'none', color: theme.colors.white, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <h2 style={{ margin: 0, color: theme.colors.white, fontSize: theme.font.size.xxl }}>Il tuo account Stripe</h2>
      </div>

      <div style={{ padding: theme.spacing.xl, paddingBottom: 80 }}>
        {loading && <p style={{ color: theme.colors.textMuted }}>Caricamento…</p>}
        {error && <p style={{ color: theme.colors.error }}>Errore: {error}</p>}

        {!loading && (
          <div style={{ border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg, padding: theme.spacing.lg, marginBottom: theme.spacing.lg }}>
            <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, marginBottom: 4 }}>Disponibile per il prelievo</div>
            <div style={{ fontSize: theme.font.size.xxl, fontWeight: 700, color: theme.colors.primary, marginBottom: theme.spacing.md }}>
              {availableEuro !== null ? `€${availableEuro}` : '—'}
            </div>

            <button
              onClick={handlePayout}
              disabled={!canPayout || payingOut}
              style={{
                width: '100%',
                background: canPayout && !payingOut ? theme.colors.primary : '#ccc',
                color: theme.colors.white,
                border: 'none',
                borderRadius: theme.radius.sm,
                padding: 12,
                fontSize: theme.font.size.md,
                fontWeight: 700,
                cursor: canPayout && !payingOut ? 'pointer' : 'not-allowed',
              }}
            >
              {payingOut ? 'Prelievo in corso...' : '💸 Preleva ora'}
            </button>

            {!canPayout && !payingOut && (
              <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, marginTop: 8 }}>
                Nessun saldo disponibile al momento.
              </div>
            )}

            {payoutMessage && (
              <div style={{
                marginTop: theme.spacing.md,
                padding: theme.spacing.sm,
                borderRadius: theme.radius.sm,
                background: payoutMessage.type === 'success' ? theme.colors.primaryLight : '#fdecea',
                color: payoutMessage.type === 'success' ? theme.colors.primary : theme.colors.error,
                fontSize: theme.font.size.sm,
              }}>
                {payoutMessage.text}
              </div>
            )}
          </div>
        )}

        {stripeConnectInstance && (
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            <ConnectBalances />
            <div style={{ marginTop: theme.spacing.xl }}>
              <ConnectAccountManagement />
            </div>
          </ConnectComponentsProvider>
        )}
      </div>

      <BottomNav />
    </div>
  );
}